import React from 'react';
import { Stack } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { capitalize } from 'lodash';

import Button from '@components/Button';
import PrivateCSDPAttachmentsTile from '@containers/crm/PrivateCSDPTiles/PrivateCSDPAttachmentsTile';

import ClippedText from '@components/ClippedText';

import { useAuthManagerContext } from '@private-crm-app/auth-manager-context';
import { trackViewAttachment } from '@private-crm-app/utils';

import { pluralize } from '@utils/AppUtil';

import SubmissionStaticMap from './SubmissionStaticMap';
import FollowerUpdates from './FollowerUpdates';

import './style.scss';

export interface ISubmissionDetailBase {
  id?: number;
  description: string;
  location?: string | null;
  coordinates?: {
    latitude: number;
    longitude: number;
    userPinLatitude: number | null;
    userPinLongitude: number | null;
  } | null;
  category: { name: string };
  issueSummary: {
    CSNumber: string;
    CSAge: number;
    status: string;
    createdAt: string;
    assignee: { firstName: string; lastName: string; isActive?: boolean } | null | undefined;
  };
  attachments?: Array<{ id: number; fileName: string; fileURL: string; mimeType?: string }>;
  isAnonymous?: boolean;
  customerInfo?: { name: string } | null;
  followerCount?: number;
  isFollowing?: boolean | null;
  isSubmitter?: boolean | null;
  followerEmails?: IFollowerUpdateEmail[];
}

export default function PrivateSubmissionDetails({
  submission,
  location,
  children,
  backURL = null,
  showName = true,
  showFollowFeature = false,
  hideUpdates = false,
  onFollowClick,
  onUnfollowClick,
}: {
  submission: ISubmissionDetailBase;
  location?: string;
  children?: React.ReactNode;
  backURL?: string | null;
  showName?: boolean;
  showFollowFeature?: boolean;
  hideUpdates?: boolean;
  onFollowClick?: () => void;
  onUnfollowClick?: () => void;
}) {
  const { isAuthenticated } = useAuthManagerContext();

  const status = submission?.issueSummary.status?.toLowerCase();
  const lat = submission.coordinates?.userPinLatitude ?? submission.coordinates?.latitude;
  const long = submission.coordinates?.userPinLongitude ?? submission.coordinates?.longitude;
  const showMap = lat != null && long != null;

  const followerCount = submission.followerCount ?? 0;
  const isFollowing = submission.isFollowing ?? null;
  const isSubmitter = submission.isSubmitter ?? null;
  const showFollowSection = showFollowFeature && submission.followerCount !== undefined;
  const followerEmails = submission.followerEmails ?? [];
  // Public Updates are visible for all non-submitters, unless explicitly hidden
  // (e.g. when the Messages panel is rendered for authenticated users).
  const showUpdatesSection = showFollowFeature && isSubmitter !== true && !hideUpdates;

  return (
    <>
      <header className="submission-detail__header">
        {backURL ? (
          <Link to={backURL} data-testid="back-arrow-link">
            <span className={'back-arrow'} />
            Back to all requests
          </Link>
        ) : isAuthenticated ? (
          <Link to={'/private-submission/listing'}>
            <span className={'back-arrow'} />
            Back to all requests
          </Link>
        ) : null}
      </header>
      <section className="submission-detail__container">
        <header className="submission-detail__info-header">
          <div className="submission-detail__info-header-left">
            <h1 className="submission-detail__info-header-title">Request #{submission?.issueSummary.CSNumber}</h1>
            <div className="submission-detail__info-header-status">
              <span className={`submission-detail__status-dot submission-detail__status-dot--${status}`} />
              <span className={`submission-detail__status-label submission-detail__status-label--${status}`}>
                {capitalize(submission?.issueSummary.status)}
              </span>
            </div>
          </div>

          {showFollowSection && (
            <div className="submission-detail__follow-section">
              <span className="submission-detail__follower-count">
                {followerCount} {followerCount === 1 ? 'Follower' : 'Followers'}
              </span>
              <div className="submission-detail__follow-action">
                {isSubmitter === true ? (
                  <span className="submission-detail__submitted-by-you">Submitted by you</span>
                ) : isFollowing ? (
                  <>
                    <span className="submission-detail__following-label">&#10003; Following</span>
                    <span className="submission-detail__follow-separator" aria-hidden="true">&middot;</span>
                    <button
                      type="button"
                      className="submission-detail__unfollow-btn"
                      onClick={onUnfollowClick}
                      aria-label="Unfollow this submission"
                    >
                      Unfollow
                    </button>
                  </>
                ) : (
                  <Button
                    bsStyle={ButtonStyle.DEFAULT}
                    content="Follow"
                    className="submission-detail__follow-btn"
                    onClick={onFollowClick}
                    aria-label="Follow this submission"
                  />
                )}
              </div>
            </div>
          )}
        </header>

        <div className="submission-detail__summary">
          {showName && (
            <div className="submission-detail-tile">
              <span className="submission-detail__summary-label">Name</span>
              <span>{submission.isAnonymous ? 'Anonymous' : submission.customerInfo?.name}</span>
            </div>
          )}
          <div className="submission-detail-tile">
            <span className="submission-detail__summary-label">Assigned to</span>
            <span>
              {submission.issueSummary.assignee
                ? `${submission.issueSummary.assignee.firstName} ${submission.issueSummary.assignee.lastName}${
                    !submission.issueSummary.assignee.isActive ? ' (Deactivated)' : ''
                  }`
                : 'Unassigned'}
            </span>
          </div>
          <div className="submission-detail-tile">
            <span className="submission-detail__summary-label">Category</span>
            <span>{submission.category.name}</span>
          </div>
          <div className="submission-detail-tile">
            <span className="submission-detail__summary-label">Submission Age</span>
            <span>{pluralize(submission.issueSummary.CSAge ?? 0, 'Day', 'Days')}</span>
          </div>
        </div>
        <div className="submission-detail__date-location">
          <Stack direction="horizontal" gap={1}>
            <span className="calendar-icon" />
            <span>Submitted {submission.issueSummary.createdAt}</span>
          </Stack>
          {location || submission.location ? (
            <Stack direction="horizontal" gap={1}>
              <span className="map-icon" />
              <span>{location || submission.location}</span>
            </Stack>
          ) : null}
        </div>

        {showMap && <SubmissionStaticMap lat={lat} long={long} />}

        <Stack className="submission-detail__description">
          <h2 className="submission-detail__description-label">Description</h2>
          <ClippedText text={submission.description} clipAfter={500} />
        </Stack>

        <Stack className="submission-detail__attachments">
          <h2 className="submission-detail__attachments-label">
            {pluralize((submission.attachments ?? []).length, 'Attachment', 'Attachments', true)}
          </h2>
          <div className="photos-wrapper">
            <PrivateCSDPAttachmentsTile
              attachments={(submission.attachments ?? []) as ICSAttachmentResponse[]}
              trackViewAttachment={trackViewAttachment}
            />
          </div>
        </Stack>

        {showUpdatesSection && <FollowerUpdates updates={followerEmails} />}

        {children}
      </section>
    </>
  );
}
